import { MAX_TEXT_LENGTH } from "../../../utils/questionUtils";
import { IdentifiableAnswerV3Input, WorkflowName } from "../../../apis/API";
import { WorkflowItem, WorkflowSpec } from "./types";
import {
  MAX_WORKFLOW_ANSWERS_BYTES,
  parseRepeatableItems,
  parseWorkflowPrepopulation,
} from "./workflowAnswers";

/**
 * Workflows whose form is seeded from the participant's last submitted answer
 * (`questionPrepopulation.values`), matching the backend's
 * `PROFILE_INFO_WORKFLOWS` and the Android/iOS `prepopulatesFromHistory` flag.
 *
 * Kept as a name set rather than a `WorkflowSpec` flag: the registry builds the
 * free-text and program-list specs from shared factories, so a spec flag would
 * also switch on every other workflow those factories produce.
 */
export const HISTORY_PREFILL_WORKFLOWS: ReadonlySet<WorkflowName> = new Set([
  WorkflowName.householdMembers,
  WorkflowName.school,
  WorkflowName.vehicles,
  WorkflowName.medications,
  WorkflowName.treatmentPrograms,
  WorkflowName.naAa,
  WorkflowName.mentalHealthTreatment,
  WorkflowName.sexOffenderTreatment,
  WorkflowName.communityService,
  WorkflowName.childSupport,
  WorkflowName.feesRestitution,
  WorkflowName.soChurch,
]);

const isEligible = (workflowName: WorkflowName): boolean =>
  HISTORY_PREFILL_WORKFLOWS.has(workflowName);

// `length` (UTF-16 units) never exceeds the UTF-8 byte count, so this O(1)
// check can't reject a payload the backend's 100 KB limit accepted. It only
// bounds parse cost for a corrupt one.
const isOversized = (json: string): boolean =>
  json.length > MAX_WORKFLOW_ANSWERS_BYTES;

/**
 * Which source seeds the form: the in-session answer (`defaultAnswer`) or the
 * history. The in-session answer wins when it holds entered content: an
 * affirmative answer, or any DIRECT_LIST answer (Save, or the skip's empty
 * list, so re-entry after "I live alone - skip" stays blank). After an
 * in-session "No" the history comes back, matching Android, where No then Yes
 * re-seeds the entry. Non-history workflows always use the in-session answer,
 * leaving their behavior untouched.
 */
export const pickSeedSource = (
  spec: WorkflowSpec,
  workflowName: WorkflowName,
  defaultAnswer?: IdentifiableAnswerV3Input,
): "answer" | "history" => {
  if (!isEligible(workflowName)) {
    return "answer";
  }
  if (!defaultAnswer) {
    return "history";
  }
  if (spec.template === "DIRECT_LIST" || defaultAnswer.answer === true) {
    return "answer";
  }
  return "history";
};

/**
 * Normalized history entries for a list or repeatable structured form.
 * Returns `[]` when the workflow isn't eligible or the history is missing,
 * oversized, invalid or empty.
 *
 * Values are trimmed, and an entry is dropped when every required field is
 * blank (for vehicles this is StructuredForm's `rowIsDroppable`, which would
 * drop it on Save anyway). If any value exceeds `MAX_TEXT_LENGTH` (possible
 * only for submissions made before the backend enforced it), the whole history
 * is skipped: submitting it unedited would make the backend reject the entire
 * check-in, and truncating would silently alter the participant's data.
 */
export const historyItems = (
  spec: WorkflowSpec,
  workflowName: WorkflowName,
  prepopulationValues?: string | null,
): WorkflowItem[] => {
  if (
    !isEligible(workflowName) ||
    !prepopulationValues ||
    isOversized(prepopulationValues)
  ) {
    return [];
  }
  // Every workflow spec has at least one required field (see the registry's
  // `fields` / `fieldsOpt`), so this is never empty.
  const requiredKeys = spec.fields
    .filter((f) => f.required)
    .map((f) => f.fieldKey);

  const items: WorkflowItem[] = [];
  for (const parsed of parseRepeatableItems(
    prepopulationValues,
    spec,
    workflowName,
  )) {
    const item: WorkflowItem = Object.create(null);
    for (const { fieldKey } of spec.fields) {
      item[fieldKey] = (parsed[fieldKey] ?? "").trim();
    }
    if (requiredKeys.every((k) => item[k].length === 0)) {
      continue;
    }
    if (spec.fields.some((f) => item[f.fieldKey].length > MAX_TEXT_LENGTH)) {
      return [];
    }
    items.push(item);
  }
  return items;
};

/**
 * Normalized history text for a YES_NO_FREE_TEXT form. Returns `""` when the
 * workflow isn't eligible or the history is missing, oversized, invalid, blank
 * or over `MAX_TEXT_LENGTH` (see {@link historyItems}).
 */
export const historyText = (
  spec: WorkflowSpec,
  workflowName: WorkflowName,
  prepopulationValues?: string | null,
): string => {
  if (
    !isEligible(workflowName) ||
    !prepopulationValues ||
    isOversized(prepopulationValues)
  ) {
    return "";
  }
  const fieldKey = spec.fields[0].fieldKey;
  const value = (
    parseWorkflowPrepopulation(prepopulationValues, spec)[fieldKey] ?? ""
  ).trim();
  return value.length > MAX_TEXT_LENGTH ? "" : value;
};

interface SeedArgs {
  spec: WorkflowSpec;
  workflowName: WorkflowName;
  defaultAnswer?: IdentifiableAnswerV3Input;
  prepopulationValues?: string | null;
}

/** Initial entries for a list or repeatable structured form. */
export const resolveSeedItems = ({
  spec,
  workflowName,
  defaultAnswer,
  prepopulationValues,
}: SeedArgs): WorkflowItem[] =>
  pickSeedSource(spec, workflowName, defaultAnswer) === "answer"
    ? parseRepeatableItems(defaultAnswer?.workflowAnswers, spec, workflowName)
    : historyItems(spec, workflowName, prepopulationValues);

/** Initial detail text for a YES_NO_FREE_TEXT form. */
export const resolveSeedText = ({
  spec,
  workflowName,
  defaultAnswer,
  prepopulationValues,
}: SeedArgs): string => {
  if (pickSeedSource(spec, workflowName, defaultAnswer) === "history") {
    return historyText(spec, workflowName, prepopulationValues);
  }
  const fieldKey = spec.fields[0].fieldKey;
  return (
    parseWorkflowPrepopulation(defaultAnswer?.workflowAnswers, spec)[
      fieldKey
    ] ?? ""
  );
};
